import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface SavedShoppingList {
    id: string;
    name: string;
    items: Record<string, number>;
    createdAt: string; // Store as ISO string
    updatedAt: string; // Store as ISO string
    active?: boolean; // Track if this list is currently active
    is_completed?: boolean; // Whether this list has been completed
}

interface ShoppingListState {
    currentList: Record<string, number>;
    savedLists: SavedShoppingList[];
}

const initialState: ShoppingListState = {
    currentList: {},
    savedLists: []
};

const shoppingListSlice = createSlice({
    name: 'shoppingList',
    initialState,
    reducers: {
        setCurrentList: (state, action: PayloadAction<Record<string, number>>) => {
            state.currentList = action.payload;
        },
        addItem: (state, action: PayloadAction<string>) => {
            const id = action.payload;
            state.currentList[id] = (state.currentList[id] || 0) + 1;
        },
        removeItem: (state, action: PayloadAction<string>) => {
            const id = action.payload;
            if (state.currentList[id] > 1) {
                state.currentList[id] -= 1;
            } else {
                delete state.currentList[id];
            }
        },
        removeItemFromList: (state, action: PayloadAction<string>) => {
            const id = action.payload;
            delete state.currentList[id];
        },
        clearCurrentList: (state) => {
            state.currentList = {};
        },
        setSavedLists: (state, action: PayloadAction<SavedShoppingList[]>) => {
            state.savedLists = action.payload;
        },
        addSavedList: (state, action: PayloadAction<SavedShoppingList>) => {
            state.savedLists.push(action.payload);
        },
        updateSavedList: (state, action: PayloadAction<SavedShoppingList>) => {
            const index = state.savedLists.findIndex(list => list.id === action.payload.id);
            if (index !== -1) {
                state.savedLists[index] = action.payload;
            }
        },
        deleteSavedList: (state, action: PayloadAction<string>) => {
            state.savedLists = state.savedLists.filter(list => list.id !== action.payload);
        },
        loadList: (state, action: PayloadAction<SavedShoppingList>) => {
            state.currentList = action.payload.items;
        },
        setListActive: (state, action: PayloadAction<{ listId: string; active: boolean }>) => {
            const index = state.savedLists.findIndex(list => list.id === action.payload.listId);
            if (index !== -1) {
                // Set all lists to inactive first, then set the target list
                state.savedLists.forEach(list => {
                    list.active = false;
                });
                state.savedLists[index].active = action.payload.active;
            }
        }
    }
});

export const {
    setCurrentList,
    addItem,
    removeItem,
    removeItemFromList,
    clearCurrentList,
    setSavedLists,
    addSavedList,
    updateSavedList,
    deleteSavedList,
    loadList,
    setListActive
} = shoppingListSlice.actions;

export default shoppingListSlice.reducer;
